import React, { useState } from 'react';
import { IonBadge, IonIcon, IonSkeletonText, useIonRouter, useIonToast } from '@ionic/react';
import {
  home,
  personOutline,
  chevronDown,
  chevronUp,
  callOutline,
  swapVerticalOutline,
  downloadOutline,
  informationCircleOutline,
} from 'ionicons/icons';
import { useSectorMembersGrouped } from '@/hooks/useSectors';
import { exportSectorMembersGrouped } from '@/api/sectors';
import { FAMILY_ROLE_LABELS } from '@/utils/labels';
import type { Family } from '@/types/family';

interface Props {
  sectorId: string;
  sectorColor: string;
}

function computeAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

const SectorMembersGrouped: React.FC<Props> = ({ sectorId, sectorColor }) => {
  const [sort, setSort] = useState<'name' | 'address'>('name');
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const { data, isLoading } = useSectorMembersGrouped(sectorId, sort);
  const router = useIonRouter();
  const [present] = useIonToast();

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    setExporting(format);
    try {
      await exportSectorMembersGrouped(sectorId, { sort, format });
      present({
        message: format === 'pdf' ? 'Export PDF descărcat.' : 'Export Excel descărcat.',
        duration: 2000, color: 'success', position: 'bottom',
      });
    } catch {
      present({ message: 'Eroare la export.', duration: 2000, color: 'danger', position: 'bottom' });
    } finally {
      setExporting(null);
    }
  };

  const toggleFamily = (familyId: string) => {
    setExpandedFamilies((prev) => {
      const next = new Set(prev);
      if (next.has(familyId)) next.delete(familyId);
      else next.add(familyId);
      return next;
    });
  };

  const expandAll = () => {
    if (!data) return;
    if (expandedFamilies.size === data.families.length) {
      setExpandedFamilies(new Set());
    } else {
      setExpandedFamilies(new Set(data.families.map((f: Family) => f.id)));
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 20 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
            <IonSkeletonText animated style={{ width: '50%', height: 16 }} />
            <IonSkeletonText animated style={{ width: '30%', height: 12, marginTop: 6 }} />
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const { families, singles, counts } = data;
  const communionMonths = data.communion_months ?? 0;
  const allExpanded = expandedFamilies.size === families.length && families.length > 0;

  const gdprChip = (gdpr: boolean | null | undefined, withLabel = true) => {
    if (gdpr === null || gdpr === undefined) return null;
    return (
      <span style={{
        fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
        backgroundColor: gdpr ? '#dcfce7' : '#fee2e2',
        color: gdpr ? '#15803d' : '#b91c1c',
      }}>
        {withLabel ? 'GDPR ' : ''}{gdpr ? 'Da' : 'Nu'}
      </span>
    );
  };

  const cinaText = (count: number | null | undefined, withLabel = true) => {
    if (count === null || count === undefined || communionMonths === 0) return null;
    return (
      <span style={{ fontSize: 11, color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {withLabel ? 'Cina ' : ''}{count}/{communionMonths}
      </span>
    );
  };

  return (
    <div>
      {/* Controls row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 12, fontSize: 13, color: '#64748b' }}>
          <span><strong>{counts.total_members}</strong> membri total</span>
          <span><strong>{counts.families}</strong> familii</span>
          <span><strong>{counts.singles}</strong> persoane singure</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="form-btn form-btn--secondary"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null}
          >
            <IonIcon icon={downloadOutline} style={{ fontSize: 13, marginRight: 3 }} />
            {exporting === 'xlsx' ? 'Se descarca...' : 'Excel'}
          </button>
          <button
            className="form-btn form-btn--secondary"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null}
          >
            <IonIcon icon={downloadOutline} style={{ fontSize: 13, marginRight: 3 }} />
            {exporting === 'pdf' ? 'Se descarca...' : 'PDF'}
          </button>
          <button
            className="form-btn form-btn--secondary"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={expandAll}
          >
            {allExpanded ? 'Restrange' : 'Expandeaza'} toate
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <IonIcon icon={swapVerticalOutline} style={{ fontSize: 14, color: '#94a3b8' }} />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as 'name' | 'address')}
              style={{
                fontSize: 12, padding: '4px 8px', border: '1px solid #e2e8f0',
                borderRadius: 6, backgroundColor: '#fff', color: '#334155',
              }}
            >
              <option value="name">Dupa nume</option>
              <option value="address">Dupa adresa</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pastoral cases kept off the registry — never remove people silently. */}
      {counts.excluded_pastoral > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
            padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
            backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
          }}
        >
          <IonIcon icon={informationCircleOutline} style={{ fontSize: 16, flexShrink: 0 }} />
          <span>
            <strong>{counts.excluded_pastoral}</strong>{' '}
            {counts.excluded_pastoral === 1 ? 'persoana nu apare' : 'persoane nu apar'} in registru
            (adulti nebotezati 18+ / fii risipitori).
          </span>
          <button
            onClick={() => router.push(`/nebotezati?sector_id=${sectorId}`)}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none', padding: 0,
              cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#92400e',
              textDecoration: 'underline', whiteSpace: 'nowrap', fontFamily: 'inherit',
            }}
          >
            Vezi lista
          </button>
        </div>
      )}

      {/* Families section */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <IonIcon icon={home} style={{ fontSize: 16, color: sectorColor }} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Familii</h3>
          <IonBadge color="medium" style={{ fontSize: 11 }}>{counts.families}</IonBadge>
        </div>

        {families.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>
            Nicio familie in acest sector.
          </div>
        ) : (
          <div className="crm-card" style={{ overflow: 'hidden' }}>
            {families.map((family: Family, idx: number) => {
              const isExpanded = expandedFamilies.has(family.id);
              const memberCount = family.member_count ?? family.members?.length ?? 0;

              return (
                <div key={family.id}>
                  {/* Family header row */}
                  <div
                    onClick={() => toggleFamily(family.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 16px', cursor: 'pointer',
                      backgroundColor: isExpanded ? '#f8fafc' : '#fff',
                      borderBottom: idx < families.length - 1 || isExpanded ? '1px solid #f1f5f9' : 'none',
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        backgroundColor: `${sectorColor}15`, color: sectorColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <IonIcon icon={home} style={{ fontSize: 14 }} />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                          {family.display_name ?? family.name}
                        </div>
                        {family.address && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {[family.address, family.city].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <IonBadge color="medium" style={{ fontSize: 10 }}>
                        {memberCount} {memberCount === 1 ? 'membru' : 'membri'}
                      </IonBadge>
                      <IonIcon
                        icon={isExpanded ? chevronUp : chevronDown}
                        style={{ fontSize: 14, color: '#94a3b8' }}
                      />
                    </div>
                  </div>

                  {/* Expanded member rows */}
                  {isExpanded && family.members && (
                    <div style={{ backgroundColor: '#fafbfc' }}>
                      {family.members.map((m) => {
                        const age = m.age;
                        return (
                          <div
                            key={m.id}
                            onClick={(e) => { e.stopPropagation(); router.push(`/members/${m.id}`); }}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '8px 16px 8px 58px', cursor: 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 500, color: '#334155' }}>
                                {m.first_name} {m.last_name}
                              </span>
                              {m.family_role && (
                                <span style={{
                                  fontSize: 10, padding: '1px 6px', borderRadius: 4,
                                  backgroundColor: m.family_role === 'head' ? '#dbeafe' : '#f1f5f9',
                                  color: m.family_role === 'head' ? '#1d4ed8' : '#64748b',
                                  fontWeight: 600,
                                }}>
                                  {FAMILY_ROLE_LABELS[m.family_role] ?? m.family_role}
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, fontSize: 12 }}>
                              {age !== null && (
                                <span style={{ color: '#64748b' }}>{age} ani</span>
                              )}
                              {m.cell_phone && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#94a3b8' }}>
                                  <IonIcon icon={callOutline} style={{ fontSize: 11 }} />
                                  {m.cell_phone}
                                </span>
                              )}
                              {gdprChip(m.gdpr)}
                              {cinaText(m.communion_count)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Singles section */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <IonIcon icon={personOutline} style={{ fontSize: 16, color: '#7c3aed' }} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Persoane Singure</h3>
          <IonBadge color="medium" style={{ fontSize: 11 }}>{counts.singles}</IonBadge>
        </div>

        {singles.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>
            Nicio persoana singura in acest sector.
          </div>
        ) : (
          <div className="crm-card" style={{ overflow: 'auto' }}>
            <table className="crm-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 180 }}>Nume</th>
                  <th style={{ width: 70 }}>Varsta</th>
                  <th style={{ width: 60 }}>Gen</th>
                  <th style={{ width: 140 }}>Telefon</th>
                  <th style={{ width: 80 }}>GDPR</th>
                  <th style={{ width: 80 }}>Cina</th>
                </tr>
              </thead>
              <tbody>
                {singles.map((s) => {
                  const age = s.age ?? computeAge(s.birth_date);
                  return (
                    <tr
                      key={s.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => router.push(`/members/${s.id}`)}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: 7,
                            backgroundColor: '#7c3aed15', color: '#7c3aed',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}>
                            <IonIcon icon={personOutline} style={{ fontSize: 13 }} />
                          </div>
                          <span className="crm-table__name">
                            {s.first_name} {s.last_name}
                          </span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {age !== null ? `${age} ani` : '\u2014'}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {s.gender === 'M' ? 'Barbat' : s.gender === 'F' ? 'Femeie' : '\u2014'}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {s.cell_phone || '\u2014'}
                      </td>
                      <td>{gdprChip(s.gdpr, false) ?? <span style={{ color: '#cbd5e1' }}>{'\u2014'}</span>}</td>
                      <td>
                        {cinaText(s.communion_count, false) ?? <span style={{ color: '#cbd5e1' }}>{'\u2014'}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectorMembersGrouped;
